/**
 * Clinical data modules: visits, notes, vitals, definitions, readings, labUpload.
 */
import { fetchApi, fetchFileApi, API_BASE_URL, generateRequestId, ApiError } from '../client';
import { csrfHeader } from '../../csrf';
import { getFriendlyErrorMessage } from '../../errorMapping';
import type {
  Visit,
  ClinicalNote,
  ClinicalNoteCreateRequest,
  Vitals,
  CreateVitalsRequest,
  ReadingCategory,
  ReadingDefinition,
  VitalsExportRequest,
  CreateReadingRequest,
  ReadingRecord,
  ReadingValue,
} from '../../../types';
import type { PagedResponse } from '../../../types/pagination';

function readingsQuery(patientId: string, visitId?: string): string {
  const params = new URLSearchParams({ patientId });
  if (visitId) params.set('visitId', visitId);
  return params.toString();
}

export const visitsModule = {
  create: (data: Partial<Visit>) =>
    fetchApi<Visit>('/api/visits', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<Visit>) =>
    fetchApi<Visit>(`/api/visits/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  getById: (id: string) =>
    fetchApi<Visit>(`/api/visits/${id}`),

  getWithVitals: (patientId: string) =>
    fetchApi<any>(`/api/visits/patient/${patientId}`),

  /** One page of the visit history, newest first (medopd-docs 112). The unpaged call stays for trends/widgets. */
  getWithVitalsPage: (patientId: string, page: number, size: number) =>
    fetchApi<PagedResponse<any>>(`/api/visits/patient/${patientId}?page=${page}&size=${size}`),

  updateVitals: (
    visitId: string,
    vitalsData: {
      systolicBP?: number | null;
      diastolicBP?: number | null;
      pulse?: number | null;
      temperature?: number | null;
      spO2?: number | null;
      respiratoryRate?: number | null;
      weight?: number | null;
    },
    editReasonCategory?: string,
    editReasonDetail?: string,
    structuredDataJson?: string
  ) =>
    fetchApi<any>(`/api/visits/${visitId}/vitals`, {
      method: 'PATCH',
      body: JSON.stringify({ ...vitalsData, editReasonCategory, editReasonDetail, structuredDataJson }),
    }),

  getAuditTrail: (visitId: string) =>
    fetchApi<any>(`/api/visits/${visitId}/audit-trail`),

  getFieldHistory: (visitId: string, fieldName: string) =>
    fetchApi<any>(`/api/visits/${visitId}/field-history/${fieldName}`),
};

export const notesModule = {
  getByPatient: (patientId: string) =>
    fetchApi<ClinicalNote[]>(`/api/notes?patientId=${patientId}`),

  /** One page of the patient's notes, newest first, paged in SQL (medopd-docs 112/117). */
  getByPatientPage: (patientId: string, page: number, size: number) =>
    fetchApi<PagedResponse<ClinicalNote>>(`/api/notes?patientId=${encodeURIComponent(patientId)}&page=${page}&size=${size}`),

  /** Notes written during one visit (ClinicalNoteController: ?visitId=). */
  getByVisit: (visitId: string) =>
    fetchApi<ClinicalNote[]>(`/api/notes?visitId=${encodeURIComponent(visitId)}`),

  create: (data: ClinicalNoteCreateRequest) =>
    fetchApi<ClinicalNote>('/api/notes', { method: 'POST', body: JSON.stringify(data) }),
};

export const vitalsModule = {
  getByPatient: (patientId: string) =>
    fetchApi<Vitals[]>(`/api/vitals?patientId=${patientId}`),

  create: (data: CreateVitalsRequest) =>
    fetchApi<Vitals>('/api/vitals', { method: 'POST', body: JSON.stringify(data) }),

  /** Readings recorded against one inpatient admission (nurse vitals, ward rounds), newest first. */
  getByAdmission: (admissionId: string) =>
    fetchApi<Vitals[]>(`/api/vitals?admissionId=${encodeURIComponent(admissionId)}&limit=50`),
};

export const definitionsModule = {
  get: (category: ReadingCategory) =>
    fetchApi<ReadingDefinition[]>(`/api/definitions?category=${category}`),
};

// Definition-based readings store (ClinicalReadingController, /api/readings). Not the /api/vitals
// intake log and not the per-visit vitals on Visit — see medopd-docs vitals notes.
export const readingsModule = {
  getVitals: (patientId: string, visitId?: string) =>
    fetchApi<ReadingRecord>(`/api/readings/vitals?${readingsQuery(patientId, visitId)}`),

  getBloodLabs: (patientId: string, visitId?: string) =>
    fetchApi<ReadingRecord>(`/api/readings/blood-labs?${readingsQuery(patientId, visitId)}`),

  getUrineLabs: (patientId: string, visitId?: string) =>
    fetchApi<ReadingRecord>(`/api/readings/urine-labs?${readingsQuery(patientId, visitId)}`),

  /** One page of one category's readings, newest first (medopd-docs 117) — every reading, not only the newest 200. */
  getPage: (category: ReadingCategory, patientId: string, page: number, size: number) => {
    const path = category === 'VITAL' ? 'vitals' : category === 'BLOOD' ? 'blood-labs' : 'urine-labs';
    return fetchApi<PagedResponse<ReadingValue>>(
      `/api/readings/${path}?${readingsQuery(patientId)}&page=${page}&size=${size}`);
  },

  createVitals: (data: CreateReadingRequest) =>
    fetchApi<any>('/api/readings/vitals', { method: 'POST', body: JSON.stringify(data) }),

  createBloodLabs: (data: CreateReadingRequest) =>
    fetchApi<any>('/api/readings/blood-labs', { method: 'POST', body: JSON.stringify(data) }),

  createUrineLabs: (data: CreateReadingRequest) =>
    fetchApi<any>('/api/readings/urine-labs', { method: 'POST', body: JSON.stringify(data) }),

  exportVitals: (data: VitalsExportRequest) =>
    fetchFileApi('/api/readings/vitals/export', data),
};

/** A file inside an upload session (fields the archive views read). */
export interface LabUploadedFile {
  id: string;
  fileName?: string;
  filePath: string;
  processingStatus?: string;
  createdAt?: string;
  extractedVitals?: unknown;
  extractedLabs?: unknown;
  extractedMedications?: unknown;
}

/** An upload session as GET /lab-upload/patient/{id} returns it; legacy single-file sessions carry the file inline. */
export interface LabUploadSessionSummary extends Partial<Omit<LabUploadedFile, 'id'>> {
  id: string;
  doctorId?: string;
  status?: string;
  documents?: LabUploadedFile[];
}

export const labUploadModule = {
  createSession: (patientId: string, consentGiven: boolean) =>
    fetchApi<{ sessionId: string; token: string; expiresAt: string }>(
      `/api/lab-upload/session?patientId=${patientId}&consentGiven=${consentGiven}`,
      { method: 'POST' }
    ),

  invalidateSession: (sessionId: string) =>
    fetchApi<void>(`/api/lab-upload/session/${sessionId}/invalidate`, { method: 'POST' }),

  finalizeSession: (sessionId: string) =>
    fetchApi<void>(`/api/lab-upload/session/${sessionId}/finalize`, { method: 'POST' }),

  getUploadUrl: (token: string) => `${API_BASE_URL}/api/lab-upload/upload/${token}`,

  getPreviewUrl: (filePath: string) =>
    fetchApi<{ url: string }>(
      `/api/lab-upload/preview?filePath=${encodeURIComponent(filePath)}`
    ),

  getPatientUploads: (patientId: string) =>
    fetchApi<any[]>(`/api/lab-upload/patient/${patientId}`),

  /** One page of the patient's upload sessions (each with its documents), newest first (medopd-docs 117). */
  getPatientUploadsPage: (patientId: string, page: number, size: number) =>
    fetchApi<PagedResponse<LabUploadSessionSummary>>(
      `/api/lab-upload/patient/${encodeURIComponent(patientId)}?page=${page}&size=${size}`),

  downloadFile: async (filePath: string) => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/octet-stream' };
      const response = await fetch(
        `${API_BASE_URL}/api/lab-upload/download?filePath=${encodeURIComponent(filePath)}`,
        { headers, credentials: 'include' } // cookie auth (GET → no CSRF needed)
      );
      if (!response.ok) throw new Error(`Failed to download: ${response.statusText}`);
      return { data: await response.blob() };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Download failed' };
    }
  },

  directUpload: async (patientId: string, files: File[], consentGiven: boolean) => {
    const formData = new FormData();
    formData.append('patientId', patientId);
    formData.append('consentGiven', consentGiven.toString());
    files.forEach(file => formData.append('file', file));

    const headers: Record<string, string> = {
      'X-Request-ID': generateRequestId(),
      'X-Client-Version': '1.0.0',
      ...csrfHeader('POST'),
    };

    try {
      const response = await fetch(`${API_BASE_URL}/api/lab-upload/direct-upload`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include', // cookie auth + CSRF token above
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new ApiError(err.message || 'Upload failed', response.status);
      }
      const data = await response.json();
      return { data: data.data };
    } catch (error) {
      if (error instanceof ApiError) return { error: getFriendlyErrorMessage(error.message) };
      return { error: 'Network error' };
    }
  },

  getExtractionSummary: (patientId: string) =>
    fetchApi<any>(`/api/lab-upload/patient/${patientId}/extraction-summary`),

  getExtractionHistory: (patientId: string, limit = 10) =>
    fetchApi<any[]>(
      `/api/lab-upload/patient/${patientId}/extraction-history?limit=${limit}`
    ),

  getTrendData: (patientId: string, metric: string, months = 6) =>
    fetchApi<any>(
      `/api/lab-upload/patient/${patientId}/trend-data?metric=${metric}&months=${months}`
    ),

  verifyExtraction: (docId: string, confirmed: boolean, notes = '') =>
    fetchApi<void>(`/api/lab-upload/${docId}/verify-extraction`, {
      method: 'POST',
      body: { confirmed, notes } as any,
    }),

  updateExtraction: (
    docId: string,
    extractedVitals: any,
    extractedLabs: any,
    extractedMedications: any
  ) =>
    fetchApi<any>(`/api/lab-upload/${docId}/extraction`, {
      method: 'PUT',
      body: { extractedVitals, extractedLabs, extractedMedications } as any,
    }),

  getClinicalSummary: (patientId: string) =>
    fetchApi<any>(`/api/lab-upload/patient/${patientId}/clinical-summary`),

  analyzeDocument: (docId: string) =>
    fetchApi<any>(`/api/lab-upload/${docId}/analyze`, { method: 'POST' }),

  generateSummary: (docId: string, patientId: string) =>
    fetchApi<any>(
      `/api/lab-upload/${docId}/generate-summary?patientId=${patientId}`,
      { method: 'POST' }
    ),
};
